import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Tables, Database } from '../types/supabase';
import EventRouteMap from '../components/EventRouteMap';
import ElevationProfile from '../components/ElevationProfile';

type RaceEvent = Tables<'race_event'>;
type Route = Tables<'route'>;
type Summit = Database['cq_static']['Tables']['summit']['Row'] & { 
  score_with_bonus?: number; 
  bonus?: number; 
  score?: number; 
};

// Custom hook for growing counter animation
const useGrowingCounter = (endValue: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [endValue, duration]);

  return count;
};

// Declare the global window property for server-provided data
declare global {
  interface Window {
    __INITIAL_EVENT_DATA__?: {
      event: RaceEvent;
      route: Route | null;
      summits: Summit[];
    };
  }
}

const EventsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<RaceEvent | null>(null);
  const [route, setRoute] = useState<Route | null>(null);
  const [summits, setSummits] = useState<Summit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trackingPosition, setTrackingPosition] = useState<{ distance: number; elevation: number; coordinates: [number, number] } | null>(null);
  const [profileHoverPosition, setProfileHoverPosition] = useState<{ distance: number; elevation: number; coordinates: [number, number] } | null>(null);

  // Helper function to get translation string
  const getTranslationString = (value: string | string[]): string => {
    return Array.isArray(value) ? value[0] : value;
  };

  // Helper function to format date in current language
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    if (language === 'fr') {
      return date.toLocaleDateString('fr-FR', options);
    } else {
      return date.toLocaleDateString('en-US', options);
    }
  };

  // Growing counter animations for stats
  const animatedDistance = useGrowingCounter(route?.distance ? Math.round(route.distance / 1000 * 10) / 10 : 0, 2000);
  const animatedElevation = useGrowingCounter(route?.elevation_gain ? Math.round(route.elevation_gain) : 0, 2000);
  const animatedScore = useGrowingCounter(route?.score ? Math.round(route.score) : 0, 2000);
  const animatedSummits = useGrowingCounter(summits.length, 2000);

  // Handle map hover
  const handleMapHover = useCallback((position: { distance: number; elevation: number; coordinates: [number, number] } | null) => {
    console.log('Map hover:', position);
    setTrackingPosition(position);
    // Don't clear profile hover when map is hovered - let them coexist
  }, []);

  // Handle profile hover
  const handleProfileHover = useCallback((position: { distance: number; elevation: number; coordinates: [number, number] } | null) => {
    console.log('Profile hover received:', position);
    setProfileHoverPosition(position);
    // Don't clear map tracking when profile is hovered - let them coexist
  }, []);

  useEffect(() => {
    // Check if we have server-provided data
    if (window.__INITIAL_EVENT_DATA__) {
      const { event: eventData, route: routeData, summits: summitsData } = window.__INITIAL_EVENT_DATA__;
      setEvent(eventData);
      setRoute(routeData);
      setSummits(summitsData);
      setLoading(false);
      
      // Clear the global data to prevent memory leaks
      delete window.__INITIAL_EVENT_DATA__;
    } else {
      // Fallback: no server data available
      setError('Event data not available');
      setLoading(false);
    }
  }, [id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">{t('events.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-red-800 mb-4">{t('events.error.title')}</h1>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-yellow-800 mb-4">{t('events.notFound.title')}</h1>
          <p className="text-red-700">{t('events.notFound.message')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-page">
      <main>
        {/* Hero Section - Event Overview */}
        <section className="event-hero" aria-labelledby="event-title">
          <div className="hero-content">
            <h1 id="event-title">{event.name}</h1>
            
            {event.registration_url && (
              <div className="hero-cta">
                <a 
                  href={event.registration_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="registration-button"
                  data-umami-event="outbound-link-click"
                  data-umami-event-url={event.registration_url}
                >
                  {t('events.hero.registerNow')}
                </a>
              </div>
            )}
            
            <div className="hero-meta">
              <div className="meta-item">
                <span className="meta-icon">📅</span>
                <span className="meta-text">
                  {formatDate(event.start_date)}
                </span>
              </div>
              
              {event.start_location && (
                <div className="meta-item">
                  <span className="meta-icon">📍</span>
                  <span className="meta-text">{event.start_location}</span>
                </div>
              )}
              
              {event.start_country && (
                <div className="meta-item">
                  <span className="meta-icon">🌍</span>
                  <span className="meta-text">{event.start_country}</span>
                </div>
              )}
            </div>
          </div>
        </section>
        
        {/* Summits Detail - Route Challenges - Moved here below hero */}
        {summits.length > 0 && (
          <section className="summits-detail" aria-labelledby="summits-detail-title">
            <h2>{t('events.summits.title')}</h2>
            <p className="section-intro">
              {getTranslationString(t('events.summits.intro'))
                .replace('{{count}}', summits.length.toString())
                .replace('{{plural}}', summits.length > 1 ? (t('events.summits.title') === 'Les Cols' ? 's' : 'es') : '')
                .replace('{{totalScore}}', summits.reduce((total, summit) => total + (summit.score_with_bonus || 0), 0).toString())}
            </p>
            
            <div className="summits-grid">
              {summits.map((summit, index) => (
                <div key={summit.id} className="summit-item">
                  <div className="summit-badge">
                    <div className="summit-name">{summit.name}</div>
                    <div className="summit-score">{Math.round(summit.score || summit.elevation)} {t('events.summits.points')}</div>
                    {summit.bonus && summit.bonus > 0 && (
                      <div className="summit-bonus">+{summit.bonus} {t('events.summits.bonus')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Event Description */}
        {event.description && (
          <section className="event-description">
            <h2>{t('events.description.title')}</h2>
            <div className="description-content">
              <p>{event.description}</p>
            </div>
          </section>
        )}
        
        {/* Route Overview - Key Stats */}
        {route && (
          <section className="route-overview" aria-labelledby="route-overview-title">
            <h2>{t('events.routeOverview.title')}</h2>
            <div className="route-stats-cards">
              <div className="stat-card secondary">
                <div className="stat-icon">📏</div>
                <div className="stat-content">
                  <div className="stat-value">
                    {animatedDistance} {t('events.routeOverview.distance')}
                  </div>
                  <div className="stat-label">{t('events.routeOverview.totalDistance')}</div>
                </div>
              </div>
              
              <div className="stat-card secondary">
                <div className="stat-icon">⛰️</div>
                <div className="stat-content">
                  <div className="stat-value">
                    {animatedElevation} {t('events.routeOverview.elevation')}
                  </div>
                  <div className="stat-label">{t('events.routeOverview.elevationGain')}</div>
                </div>
              </div>
              
              {route.score && (
                <div className="stat-card primary">
                  <div className="stat-icon">🏆</div>
                  <div className="stat-content">
                    <div className="stat-value">{summits.reduce((total, summit) => total + (summit.score_with_bonus || 0), 0)}</div>
                    <div className="stat-label">{t('events.routeOverview.minimumScore')}</div>
                  </div>
                </div>
              )}
              
              {summits.length > 0 && (
                <div className="stat-card primary">
                  <div className="stat-icon">🚩</div>
                  <div className="stat-content">
                    <div className="stat-value">{animatedSummits}</div>
                    <div className="stat-label">{t('events.routeOverview.cols')}</div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
        
        {/* Route Visualization - Map + Elevation Profile */}
        {(route || summits.length > 0) && (
          <section className="route-visualization">
            <h2>{t('events.routeVisualization.title')}</h2>
            
            {/* Wrapped content with neobrutal styling */}
            <div className="route-visualization-content">
              {/* Interactive Map */}
              <div className="map-section">
                <EventRouteMap 
                  route={route} 
                  summits={summits} 
                  eventName={event.name}
                  onMapHover={handleMapHover}
                  profileHoverPosition={profileHoverPosition}
                />
              </div>
              
              {/* Elevation Profile - Integrated with map */}
              {route && (
                <div className="elevation-section">
                  <ElevationProfile 
                    route={route} 
                    onProfileHover={handleProfileHover}
                    mapHoverPosition={trackingPosition}
                  />
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default EventsPage; 